import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: '/api',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('redex_admin_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('redex_admin_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface SitesResponse {
  success: boolean;
  data: {
    data: any[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
}

export interface SiteResponse {
  success: boolean;
  data: any;
}

export interface DomainConfigResponse {
  success: boolean;
  data: {
    domain: string;
    sslStatus: {
      isValid: boolean;
      expiry: string | null;
      error: string | null;
    };
    dnsStatus: {
      resolved: boolean;
      ip: string | null;
      error: string | null;
    };
    lastChecked: string;
  };
}

export interface FileStructureResponse {
  success: boolean;
  data: {
    sitePath: string;
    fileStructure: any[];
    domain: string;
  };
}

export interface PublishResponse {
  success: boolean;
  message: string;
  data?: any;
}

export const sitesService = {
  /**
   * Get paginated list of sites
   */
  async getSites(params?: {
    page?: number;
    limit?: number;
    search?: string;
    clientId?: string;
    status?: string;
    layout?: string;
  }): Promise<SitesResponse> {
    const searchParams = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, value.toString());
        }
      });
    }
    
    const queryString = searchParams.toString();
    const url = `/sites${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url);
    return response.data;
  },

  /**
   * Get a specific site by ID
   */
  async getSite(id: string): Promise<SiteResponse> {
    const response = await api.get(`/sites/${id}`);
    return response.data;
  },

  /**
   * Get domain configuration and SSL status
   */
  async getDomainConfig(id: string): Promise<DomainConfigResponse> {
    const response = await api.get(`/sites/${id}/domain-config`);
    return response.data;
  },

  /**
   * Update/renew SSL certificate
   */
  async updateSSL(id: string): Promise<PublishResponse> {
    const response = await api.post(`/sites/${id}/update-ssl`);
    return response.data;
  },

  /**
   * Get site file structure
   */
  async getFileStructure(id: string): Promise<FileStructureResponse> {
    const response = await api.get(`/sites/${id}/file-structure`);
    return response.data;
  },

  /**
   * Update site content
   */
  async updateContent(id: string, data: {
    config_json?: any;
    autoPublish?: boolean;
  }): Promise<SiteResponse> {
    const response = await api.patch(`/sites/${id}/content`, data);
    return response.data;
  },

  /**
   * Publish site to live
   */
  async publishSite(id: string): Promise<PublishResponse> {
    const response = await api.post(`/sites/${id}/publish`);
    return response.data;
  },

  /**
   * Create a new site
   */
  async createSite(data: {
    name: string;
    clientId: string;
    layout: string;
    domain?: string;
    config_json?: any;
  }): Promise<SiteResponse> {
    const response = await api.post('/sites', data);
    return response.data;
  },

  /**
   * Update an existing site
   */
  async updateSite(id: string, data: {
    name?: string;
    domain?: string;
    layout?: string;
    status?: string;
    config_json?: any;
  }): Promise<SiteResponse> {
    const response = await api.put(`/sites/${id}`, data);
    return response.data;
  },

  /**
   * Delete a site
   */
  async deleteSite(id: string): Promise<{ success: boolean; message: string }> {
    const response = await api.delete(`/sites/${id}`);
    return response.data;
  },

  /**
   * Stage a site (wizard workflow)
   */
  async stageSite(data: any): Promise<SiteResponse> {
    const response = await api.post('/sites/stage', data);
    return response.data;
  }
};
